// src/lib/attributes-api.ts

/**
 * Attributes API Client
 * API functions for attribute CRUD operations
 */

import { get, post, put, del } from '@/lib/api-client'
import type {
  Attribute,
  AttributeListResponse,
  AttributeQueryParams,
  CreateAttributeRequest,
  UpdateAttributeRequest,
} from '@/types/attribute'

/**
 * Fetch list of attributes with optional filters
 */
export async function fetchAttributes(
  params: AttributeQueryParams = {}
): Promise<AttributeListResponse> {
  const queryParams = new URLSearchParams()

  if (params.page !== undefined) {
    queryParams.set('page', String(params.page))
  }
  if (params.pageSize !== undefined) {
    queryParams.set('pageSize', String(params.pageSize))
  }
  if (params.search) {
    queryParams.set('search', params.search)
  }
  if (params.category) {
    queryParams.set('category', params.category)
  }
  if (params.dataType) {
    queryParams.set('dataType', params.dataType)
  }
  if (params.sourceType) {
    queryParams.set('sourceType', params.sourceType)
  }
  if (params.isSystemManaged !== undefined) {
    queryParams.set('isSystemManaged', String(params.isSystemManaged))
  }

  const queryString = queryParams.toString()
  const url = `/attributes${queryString ? `?${queryString}` : ''}`

  const response = await get<AttributeListResponse>(url)
  return response
}

/**
 * Fetch a single attribute by ID
 */
export async function fetchAttribute(id: string): Promise<Attribute> {
  const response = await get<Attribute>(`/attributes/${id}`)
  return response
}

/**
 * Create a new attribute
 */
export async function createAttribute(
  data: CreateAttributeRequest
): Promise<Attribute> {
  const response = await post<Attribute>('/attributes', data)
  return response
}

/**
 * Update an existing attribute
 */
export async function updateAttribute(
  id: string,
  data: UpdateAttributeRequest
): Promise<Attribute> {
  const response = await put<Attribute>(`/attributes/${id}`, data)
  return response
}

/**
 * Delete an attribute (soft delete)
 */
export async function deleteAttribute(id: string): Promise<void> {
  await del(`/attributes/${id}`)
}
